import {
  Box,
  LinearProgress,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import Popup from "./model/Popup";
import { useSelector } from "react-redux";
import CustomButton from "./button/CustomButton";
import { useDispatch } from "react-redux";
import {
  setSyncBulkFaildedResponse,
  setSyncBulkInvoiceResponse,
} from "../../store/reducers/invoiceSlice";
import { useEffect, useMemo, useState } from "react";

const ProgressAlert = ({ show, setShow }: any) => {
  const { syncBulkFailedResponse, isSyncBulkInvoiceResponse } = useSelector(
    (state: any) => state.invoice
  );
  const dispatch = useDispatch();
 const [progress, setProgress] = useState(0);

  /* ---------------------------------------
   * Derive failed items SAFELY
   * --------------------------------------- */
  const failedItems = useMemo(() => {
    return (
      syncBulkFailedResponse?.filter(
        (item: any) => item.success === false
      ) || []
    );
  }, [syncBulkFailedResponse]);

  /* ---------------------------------------
   * Simulated progress bar
   * --------------------------------------- */
  useEffect(() => {
    if (!show) return;

    setProgress(0);
    const timer = setInterval(() => {
      setProgress((prev) => (prev < 90 ? prev + 10 : prev));
    }, 400);

    return () => clearInterval(timer);
  }, [show]);

  /* ---------------------------------------
   * API completed → finalize progress
   * --------------------------------------- */
  useEffect(() => {
    if (isSyncBulkInvoiceResponse) {
      setProgress(100);
    }
  }, [isSyncBulkInvoiceResponse]);

  /* ---------------------------------------
   * AUTO OPEN / CLOSE LOGIC (CRITICAL)
   * --------------------------------------- */
  useEffect(() => {
    // ⛔ Wait until API finishes
    if (!isSyncBulkInvoiceResponse) return;

    // ✅ Open modal if failures exist
    if (failedItems.length > 0) {
      setShow(true);
    } else {
      // ❌ Close modal if no failures
      setShow(false);
    }
  }, [isSyncBulkInvoiceResponse, failedItems.length, setShow]);

  /* ---------------------------------------
   * NEVER render modal if no failures
   * --------------------------------------- */
  if (!show || failedItems.length === 0) return null;

  const handleClose = () => {
    setShow(false);
    dispatch(setSyncBulkInvoiceResponse(false));
    dispatch(setSyncBulkFaildedResponse([]));
  };


  return (
    <Popup
      fullWidth
      open={show}
      title="QuickBooks Sync"
      onHide={handleClose}
      maxWidth="md"
      footer={<CustomButton title="Close" onClick={handleClose} />}
    >
      {/* 🔹 LOADER */}
      {failedItems.length > 0 && isSyncBulkInvoiceResponse && (
        <>
          <Typography sx={{ mb: 1, mt: 2, px: 2 }}>
            Syncing invoices to QuickBooks…
          </Typography>
         <LinearProgress
                variant="determinate"
                value={progress}
              />
        </>
      )}

      {/* 🔹 SUCCESS */}
      {!syncBulkFailedResponse && failedItems.length === 0 && (
        <Alert severity="success" sx={{ mt: 2, px: 2 }}>
          All invoices synced successfully 🎉
        </Alert>
      )}

      {/* 🔹 FAILED LIST */}
      {!syncBulkFailedResponse.loading && failedItems.length > 0 && (
        <Box sx={{ mt: 2, px: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Some invoices failed to sync
          </Alert>

          <List sx={{ maxHeight: 250, overflow: "auto" }}>
            {failedItems.map((item: any, index: number) => (
              <ListItem
                key={index}
                sx={{
                  border: "1px solid #f44336",
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: "#ffebee",
                }}
              >
                <ListItemText
                  primary={item.invoiceNumber}
                  secondary={item.error}
                />
                <Chip label="Failed" color="error" size="small" />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Popup>
  );
};

export default ProgressAlert;
